import { getData, postData, patchData, delData } from './core';

export type ScenarioStatus = 'DRAFT' | 'VIEW' | 'EDITING' | 'ARCHIVED';
export type DeltaChangeType = 'CREATED' | 'MODIFIED' | 'CANCELLED';

export interface ScenarioVoyageDeltaRow {
  id: string;
  voyageId: string | null;
  changeType: DeltaChangeType;
  deltaJson: Record<string, unknown> | null;
}

export interface ScenarioShipmentDeltaRow {
  id: string;
  shipmentId: string | null;
  scenarioVoyageDeltaId: string | null;
  changeType: DeltaChangeType;
  deltaJson: Record<string, unknown> | null;
}

export interface ScenarioStockDeltaRow {
  id: string;
  stockId: string | null;
  changeType: DeltaChangeType;
  deltaJson?: Record<string, unknown> | null;
}

export interface ScenarioSharedUserDTO {
  id: string;
  sharedUser: {
    emea: string;
    givenName: string | null;
    familyName: string | null;
  };
  status: ScenarioStatus;
  createdAt: string;
}

export interface ScenarioDTO {
  id: string;
  name: string;
  description: string | null;
  status: ScenarioStatus;
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
  voyageDeltas: ScenarioVoyageDeltaRow[];
  shipmentDeltas: ScenarioShipmentDeltaRow[];
  stockDeltas: ScenarioStockDeltaRow[];
  scenarioSharedUsers: ScenarioSharedUserDTO[];
}

export interface ScenarioCreateRequest {
  name: string;
  description?: string | null;
}

export interface VoyageDeltaRequest {
  deltaId?: string;
  voyageId?: string;
  changeType: DeltaChangeType;
  patch?: Record<string, unknown>;
}

export interface StockDeltaRequest {
  changeType: DeltaChangeType;
  patch?: Record<string, unknown>;
}

// ── Lifecycle (US-1) ────────────────────────────────────────────────────────

export const createScenario = (
  body: ScenarioCreateRequest
): Promise<ScenarioDTO> => postData('/scenario', body);

export const listScenarios = (): Promise<ScenarioDTO[]> => getData('/scenario');

export const getScenario = (id: string): Promise<ScenarioDTO> =>
  getData(`/scenario/${id}`);

export const deleteScenario = (id: string): Promise<void> =>
  delData(`/scenario/${id}`);

export const setScenarioStatus = (
  id: string,
  status: ScenarioStatus
): Promise<ScenarioDTO> => patchData(`/scenario/${id}/status`, { status });

export const renewEditing = (id: string): Promise<ScenarioDTO> =>
  patchData(`/scenario/${id}/renew-editing`);

// ── Delta endpoints (US-2) — used by US-5 ──────────────────────────────────
// VoyageDeltaRequest: POST is an upsert keyed by voyageId then deltaId.

export const upsertVoyageDelta = (
  scenarioId: string,
  body: VoyageDeltaRequest
): Promise<unknown> => postData(`/scenario/${scenarioId}/voyage-delta`, body);

export const deleteVoyageDelta = (
  scenarioId: string,
  deltaId: string
): Promise<void> => delData(`/scenario/${scenarioId}/voyage-delta/${deltaId}`);

export const deleteShipmentDelta = (
  scenarioId: string,
  deltaId: string
): Promise<void> =>
  delData(`/scenario/${scenarioId}/shipment-delta/${deltaId}`);

// StockDeltaRequest: no IDs — backend derives UUID from patch fields.
// Blocked deltas are synced server-side automatically inside upsertStockDelta.

export const upsertStockDelta = (
  scenarioId: string,
  body: StockDeltaRequest
): Promise<unknown> => postData(`/scenario/${scenarioId}/stock-delta`, body);

export const deleteStockDelta = (
  scenarioId: string,
  deltaId: string
): Promise<void> => delData(`/scenario/${scenarioId}/stock-delta/${deltaId}`);

// ── Sharing (US-share) ──────────────────────────────────────────────────────

export const shareScenario = (
  scenarioId: string,
  sharedUser: string
): Promise<ScenarioSharedUserDTO> =>
  postData(`/scenario/${scenarioId}/share`, { sharedUser });

export const deleteSharedUser = (
  scenarioId: string,
  shareId: string
): Promise<void> => delData(`/scenario/${scenarioId}/${shareId}`);
